<template>
  <div>
    <!-- Backdrop with blur effect -->
    <div
      v-if="visible"
      class="sidebar-backdrop"
      aria-label="Fermer l’aperçu rapide du ticket"
      @click="close"
    />

    <!-- Modern Sidebar Drawer -->
    <div
      class="sidebar-drawer"
      :class="{ 'sidebar-open': visible }"
      aria-label="Aperçu rapide du ticket de support logistique"
      role="dialog"
      aria-modal="true"
    >
      <!-- Gradient Header -->
      <div class="sidebar-header sticky-header d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div class="header-icon-wrapper brand-header-icon">
            <feather-icon
              icon="InfoIcon"
              size="20"
              aria-label="Informations du ticket"
            />
          </div>
          <div class="ticket-ref-group ml-2">
            <span class="ticket-ref">
              Ticket
              <template v-if="ticket && ticket.status && ticket.status.display_name">
                {{ ' ' + ticket.status.display_name }}
              </template>
              <template v-else-if="ticket && ticket.reference">
                #{{ ticket.reference }}
              </template>
              <template v-else>
                #{{ ticketId }}
              </template>
            </span>
            <b-button
              v-if="ticket && ticket.reference"
              v-b-tooltip.hover
              variant="link"
              class="btn-copy-ref p-0 ml-1"
              title="Copier la référence"
              aria-label="Copier la référence du ticket"
              @click="copyReference(ticket.reference)"
            >
              <feather-icon
                icon="CopyIcon"
                size="16"
              />
              <span
                v-if="copied"
                class="text-success ml-1"
              >✔</span>
            </b-button>
          </div>
        </div>
        <b-button
          variant="light-secondary"
          class="btn-icon rounded-circle"
          size="sm"
          aria-label="Fermer l’aperçu rapide du ticket"
          @click="close"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </b-button>
      </div>

      <!-- Content -->
      <div
        class="sidebar-content"
        aria-label="Contenu de l’aperçu rapide du ticket de support"
      >
        <div
          class="sr-only"
          aria-live="polite"
        >
          Vous consultez l’aperçu rapide d’un ticket de support logistique. Utilisez les boutons pour copier la référence ou fermer la fenêtre.
        </div>
        <div
          v-if="loading"
          class="text-center p-5"
        >
          <b-spinner
            variant="primary"
            label="Chargement..."
          />
        </div>
        <div
          v-else-if="error"
          class="text-center p-4 error-container"
        >
          <div class="error-icon-wrapper mb-3">
            <feather-icon
              icon="AlertCircleIcon"
              size="32"
            />
          </div>
          <p class="text-danger mb-3">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            class="btn-retry"
            @click="fetchTicket"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-1"
              size="14"
            />
            Réessayer
          </b-button>
        </div>
        <div v-else-if="ticket">
          <!-- Ticket Details Card -->
          <b-card class="modern-card enhanced-card mb-3">
            <template #header>
              <div class="card-header-content brand-card-header">
                <feather-icon
                  icon="InfoIcon"
                  class="card-header-icon brand-header-icon"
                  size="18"
                />
                <h6 class="mb-0 ml-2 brand-header-title">
                  Détails du Ticket
                </h6>
              </div>
            </template>
            <div class="row">
              <div class="col-7">
                <!-- Order BL Section -->
                <div
                  v-if="ticket && ticket.orders && ticket.orders.length"
                  class="info-item-premium mt-2"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-primary"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">BL de la commande</small>
                    <h6 class="mb-0 font-weight-bold text-primary">
                      #{{ ticket.orders[0].id }}
                    </h6>
                  </div>
                </div>
                <!-- End Order BL Section -->
                <div class="info-item-premium mt-2">
                  <feather-icon
                    icon="LayersIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-info"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Catégorie</small>
                    <h6 class="mb-0 font-weight-bold text-info">
                      {{ ticket.category ? (ticket.category.display_name || ticket.category.name) : '—' }}
                    </h6>
                    <div
                      v-if="ticket.category && ticket.category.description"
                      class="category-description mt-1"
                    >
                      {{ ticket.category.description }}
                    </div>
                  </div>
                </div>
                <div class="info-item-premium mt-2">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-success"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Créé le</small>
                    <h6 class="mb-0 font-weight-bold text-success">
                      {{ ticket.created_at | datetime }}
                    </h6>
                  </div>
                </div>
                <div class="info-item-premium mt-2">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-primary"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Dernière mise à jour</small>
                    <h6 class="mb-0 font-weight-bold text-primary">
                      {{ ticket.updated_at | datetime }}
                    </h6>
                  </div>
                </div>
                <div
                  v-if="ticket.closed_at"
                  class="info-item-premium mt-2"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-success"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Clôturé le</small>
                    <h6 class="mb-0 font-weight-bold text-success">
                      {{ ticket.closed_at | datetime }}
                    </h6>
                  </div>
                </div>
              </div>
              <div class="col-5">
                <div class="info-item-premium">
                  <feather-icon
                    icon="UserIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-purple"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Demandeur</small>
                    <h6 class="mb-0 font-weight-bold text-purple">
                      <b-avatar
                        :src="getAvatar(ticket.submitter)"
                        class="mr-1"
                        size="28"
                      />
                      {{ ticket.submitter ? ticket.submitter.username : '—' }}
                      <span
                        v-if="ticket.submitter && ticket.submitter.phone"
                        class="ml-2 text-muted"
                      >{{ ticket.submitter.phone }}</span>
                    </h6>
                  </div>
                </div>
                <div
                  v-if="showAssignee"
                  class="info-item-premium mt-2"
                >
                  <feather-icon
                    icon="UserCheckIcon"
                    size="16"
                    class="info-icon-premium bg-gradient-orange"
                  />
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Assigné à</small>
                    <h6 class="mb-0 font-weight-bold text-orange">
                      <b-avatar
                        :src="getAvatar(ticket.assignee)"
                        class="mr-1"
                        size="28"
                      />
                      {{ ticket.assignee ? ticket.assignee.username : (ticket.assigned_to_name || '—') }}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
            <div class="info-item-premium mt-2">
              <feather-icon
                icon="CheckCircleIcon"
                size="16"
                class="info-icon-premium bg-gradient-success"
              />
              <div class="info-content-premium">
                <small class="text-uppercase font-weight-bold text-muted">Statut</small>
                <h6 class="mb-0 font-weight-bold">
                  <b-badge
                    :variant="statusVariant(ticket.status)"
                    class="status-badge mr-2"
                  >
                    {{ (ticket.status && ticket.status.display_name) || ticket.status_display || (ticket.status && ticket.status.name) || ticket.status || '—' }}
                  </b-badge>
                </h6>
              </div>
            </div>
          </b-card>

          <!-- Description Card -->
          <b-card class="modern-card mb-3">
            <template #header>
              <div class="card-header-content brand-card-header">
                <feather-icon
                  icon="FileTextIcon"
                  class="card-header-icon brand-header-icon"
                  size="16"
                />
                <h6 class="mb-0 brand-header-title">
                  Description
                </h6>
              </div>
            </template>
            <div class="ticket-desc px-2 py-2">
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="ticket && ticket.description"
                v-html="sanitizedDescription"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div v-else>
                —
              </div>
            </div>
          </b-card>

          <!-- History Card -->
          <b-card class="modern-card mb-3">
            <template #header>
              <div class="card-header-content brand-card-header">
                <feather-icon
                  icon="ClockIcon"
                  class="card-header-icon brand-header-icon"
                  size="16"
                />
                <h6 class="mb-0 brand-header-title">
                  Historique
                </h6>
              </div>
            </template>
            <ul class="timeline">
              <li
                v-for="(h, i) in ticket.history || ticket.histories"
                :key="i"
                class="timeline-item"
              >
                <span class="timeline-dot" />
                <div class="timeline-content">
                  <div class="font-weight-bold">
                    {{ h.message }}
                  </div>
                  <div class="text-muted small">
                    {{ h.created_at | datetime }} - {{ h.description || '' }}
                  </div>
                </div>
              </li>
            </ul>
          </b-card>
        </div>
        <div
          v-else
          class="p-4 text-center text-muted"
        >
          No ticket data.
        </div>
      </div>

      <!-- Footer -->
      <div class="sidebar-footer sticky-footer">
        <b-button
          aria-label="Fermer l’aperçu rapide du ticket"
          @click="close"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BSpinner, BBadge, BCard, BAvatar,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import axios from '@/libs/axios'
import defaultAvatar from '@/assets/images/avatars/user.png'
import DOMPurify from '@/libs/dompurify'

export default {
  name: 'TicketQuickView',
  components: {
    BButton, BSpinner, BBadge, BCard, FeatherIcon, BAvatar,
  },
  directives: {
    'b-tooltip': {},
  },
  filters: {
    datetime(val) {
      if (!val) return ''
      return new Date(val).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },
  props: {
    ticketId: {
      type: [Number, String],
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      error: '',
      ticket: null,
      copied: false,
      orderFields: [
        { key: 'id', label: 'BL' },
        { key: 'name', label: 'Client' },
        { key: 'phone1', label: 'Téléphone' },
        { key: 'price_ttc', label: 'Montant' },
        { key: 'status', label: 'Statut' },
        { key: 'date', label: 'Date' },
      ],
    }
  },
  computed: {
    showAssignee() {
      // Only show for privileged roles
      const privilegedRoles = ['admin', 'manager', 'warehouse_operator', 'customer_support']
      const user = this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.user
      return user && user.role && privilegedRoles.includes(user.role)
    },
    sanitizedDescription() {
      return this.ticket && this.ticket.description
        ? DOMPurify.sanitize(this.ticket.description)
        : ''
    },
  },
  watch: {
    value(val) {
      this.visible = val
      if (val) this.fetchTicket()
    },
    visible(val) {
      this.$emit('input', val)
      if (!val) this.ticket = null
    },
    ticketId: {
      immediate: true,
      handler() {
        if (this.visible) this.fetchTicket()
      },
    },
  },
  methods: {
    close() {
      this.visible = false
    },
    async fetchTicket() {
      if (!this.ticketId) return
      this.loading = true
      this.error = ''
      this.ticket = null
      try {
        const { data } = await axios.get(`/api/support/tickets/${this.ticketId}/`)
        this.ticket = data
      } catch (e) {
        this.error = 'Failed to load ticket details.'
      } finally {
        this.loading = false
      }
    },
    statusVariant(status) {
      if (!status) return 'secondary'
      // Prefer display_name for UI, but use English name for logic
      const name = status.name || status
      if (name) {
        const map = {
          open: 'success',
          closed: 'danger',
          resolved: 'primary',
          in_progress: 'warning',
        }
        return map[name] || 'secondary'
      }
      return 'secondary'
    },
    getAvatar(user) {
      if (!user) return defaultAvatar
      return defaultAvatar
    },
    copyReference(ref) {
      if (!ref) return
      navigator.clipboard.writeText(ref)
      this.copied = true
      setTimeout(() => { this.copied = false }, 1200)
    },
    getOrderStatusLabel(status) {
      const map = {
        0: 'En Attente',
        1: 'En Pickup',
        2: 'Colis au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour expéditeur',
        8: 'En Transfert',
        9: 'Retour payé',
        10: 'Retour inter agence',
        11: 'Retour inter agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'A rembourser',
        17: 'Remboursé',
      }
      return map[status] !== undefined ? map[status] : status
    },
  },
}
</script>

<style scoped>
.sidebar-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 2000;
}
.sidebar-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 700px;
  height: 100%;
  background: #f8f9fa;
  box-shadow: -2px 0 18px rgba(0, 0, 0, 0.18);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 2100;
  display: flex;
  flex-direction: column;
  border-radius: 18px 0 0 18px;
}
.sidebar-open {
  transform: translateX(0);
}
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(90deg, #0A2634 0%, #1d1d1b 100%);
  color: #fff;
  border-radius: 18px 0 0 0;
  padding: 1.25rem 1.5rem;
  min-height: 70px;
  box-shadow: 0 2px 8px rgba(102,126,234,0.08);
}
.header-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255,255,255,0.13);
}
.ticket-ref-group {
  display: flex;
  align-items: center;
}
.ticket-ref {
  font-size: 1.18rem;
  font-weight: 700;
  letter-spacing: 1px;
}
.btn-copy-ref {
  color: #fff !important;
  margin-left: 0.5rem;
  font-size: 1rem;
  background: none;
  border: none;
  box-shadow: none;
}
.btn-copy-ref:hover {
  color: #00cfff !important;
}
.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  background: #f8f9fa;
}
.sidebar-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
  border-radius: 0 0 0 18px;
  display: flex;
  justify-content: flex-end;
}
.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #fff;
}
.btn-close-drawer {
  min-width: 120px;
  font-weight: 600;
  border-radius: 8px;
  transition: background 0.2s;
}
.btn-close-drawer:hover {
  background: #e2e8f0;
  color: #333;
}
.modern-card {
  border-radius: 0.75rem;
  border: none;
  background: #fff;
  margin-bottom: 1.5rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.modern-card:hover {
  box-shadow: 0 4px 18px rgba(102,126,234,0.13);
}
.card-header-content {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #fff !important;
  color: #1d1d1b !important;
  border-radius: 1rem 1rem 0 0;
  border-bottom: 1px solid #f0f0f0;
}
.card-header-icon {
  margin-right: 0.5rem;
}
.info-item-premium {
  display: flex;
  align-items: center;
  padding: 0.8rem 1.2rem;
  border-radius: 0.375rem;
  background: #fff;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.info-icon-premium {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 0.75rem;
}
.bg-gradient-indigo,
.bg-gradient-info,
.bg-gradient-success,
.bg-gradient-primary,
.bg-gradient-purple,
.bg-gradient-orange {
  background: #0A2634 !important;
  color: #fff !important;
}
.category-description {
  background: #f8f9fa;
  border-left: 3px solid #0A2634;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  color: #1d1d1b;
  font-size: 0.97rem;
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
}
.ticket-desc {
  font-size: 1.08rem;
  color: #1d1d1b;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 0.7rem 1rem;
}
.orders-table {
  font-size: 0.97rem;
}
.timeline {
  list-style: none;
  padding-left: 0;
  margin-left: 0;
  position: relative;
}
.timeline-item {
  position: relative;
  margin-bottom: 1.2rem;
  padding-left: 2rem;
}
.timeline-dot {
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 12px;
  height: 12px;
  background: #0A2634;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #0A263433;
}
.timeline-content {
  margin-left: 1.5rem;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>
